import { useState, useEffect, useRef } from "react";

const useInView = (threshold = 0.15) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, inView];
};

const FadeUp = ({ children, delay = 0, className = "" }) => {
  const [ref, inView] = useInView();
  return (
    <div ref={ref} className={className} style={{
      opacity: inView ? 1 : 0, transform: inView ? "translateY(0)" : "translateY(28px)",
      transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`
    }}>{children}</div>
  );
};

const NAV_LINKS = ["Why Us", "Services", "Approach", "Contact"];

const WHY_CARDS = [
  { num: "01", icon: "🎓", title: "Sector Experience That Matters", text: "We understand compassion, safeguarding, compliance, and person-centred support from the inside. We don't just fill shifts — we match the right professionals to the right environments." },
  { num: "02", icon: "⭐", title: "Quality Over Quantity", text: "Every candidate is carefully vetted, trained, and selected to ensure they deliver safe, respectful, and high-quality care aligned with your service standards." },
  { num: "03", icon: "🤝", title: "Consistency & Reliability", text: "We build long-term relationships, providing consistent staff who understand your service users, routines, and expectations — maintaining stability and trust." },
  { num: "04", icon: "⚡", title: "Flexible & Responsive", text: "From emergency cover to long-term solutions, we adapt quickly and efficiently to meet your needs exactly when you need it most." },
];

const SERVICES = [
  { icon: "🏥", title: "Residential & Nursing Care", text: "Skilled nurses, senior carers, and support workers for residential and nursing facilities — matched to your ethos and quality standards." },
  { icon: "🏠", title: "Domiciliary & Home Care", text: "Compassionate home care professionals who understand the importance of independence, dignity, and routine for every service user." },
  { icon: "🧠", title: "Specialist Support", text: "Staff experienced in mental health, learning disabilities, and complex care — providing tailored, sensitive, and skilled support." },
  { icon: "⚡", title: "Emergency Cover", text: "Rapid response for urgent staffing gaps. We understand the impact of short-notice absences and act fast to protect service continuity." },
  { icon: "📅", title: "Temporary & Contract", text: "Flexible placements from a single shift to extended contracts — the right professional at the right time, without compromise." },
  { icon: "🌟", title: "Permanent Recruitment", text: "Build exceptional permanent teams with candidates who share your values, vision, and commitment to outstanding care." },
];

const PILLARS = [
  { icon: "💙", title: "Compassion", text: "Every professional we place genuinely cares — not just professionally, but as a human committed to making a real difference." },
  { icon: "🎯", title: "Precision", text: "Thoughtful matching based on skills, experience, and cultural fit — not simply availability." },
  { icon: "🤲", title: "Dignity", text: "Unwavering respect for every service user. Dignity is non-negotiable in every placement we make." },
  { icon: "🔗", title: "Partnership", text: "We work alongside you as a trusted partner, not a transactional agency — invested in your long-term success." },
];

const APPROACH_CARDS = [
  { icon: "💡", title: "Insight-Driven Matching", text: "Because we've worked in care ourselves, we know how to identify professionals who truly understand their environment." },
  { icon: "🛡️", title: "Safeguarding First", text: "Rigorous compliance checks and ongoing training ensure every placement meets the highest safeguarding standards." },
  { icon: "🔄", title: "Continuity of Care", text: "We prioritise familiar faces and consistent relationships to protect service users from disruption." },
];

export default function Staff() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [form, setForm] = useState({ first: "", last: "", email: "", org: "", type: "", message: "" });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif" }} className="bg-stone-50 text-slate-800 overflow-x-hidden">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=DM+Sans:wght@300;400;500;600&display=swap');
        .font-display { font-family: 'Cormorant Garamond', serif; }
        .gold { color: #c8a96e; }
        .gold-bg { background-color: #c8a96e; }
        .gold-border { border-color: #c8a96e; }
        .teal { color: #1a6b5a; }
        .teal-bg { background-color: #1a6b5a; }
        .navy { color: #0d1f2d; }
        .navy-bg { background-color: #0d1f2d; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: #0d1f2d; }
        ::-webkit-scrollbar-thumb { background: #c8a96e; border-radius: 3px; }
        .service-card:hover { transform: translateY(-6px); box-shadow: 0 20px 50px rgba(13,31,45,0.12); }
        .service-card { transition: transform 0.3s ease, box-shadow 0.3s ease; }
        .why-card:hover { background: #f7f3ec; }
        .why-card { transition: background 0.3s; }
        .pillar-card:hover { background: rgba(200,169,110,0.08); border-color: rgba(200,169,110,0.4); }
        .pillar-card { transition: background 0.3s, border-color 0.3s; }
        input:focus, select:focus { outline: none; border-color: #c8a96e !important; }
        .strip-bg { background: linear-gradient(90deg, #1a6b5a, #0d4a3c); }
        .hero-overlay { background: linear-gradient(135deg, rgba(26,107,90,0.12) 0%, transparent 60%); }
        .cta-bg { background: linear-gradient(135deg, #1a6b5a 0%, #0d1f2d 100%); }
      `}</style>

     
      

      {/* HERO */}
      <section id="why-us" className="navy-bg min-h-screen flex items-center pt-24 pb-16 relative overflow-hidden">
        <div className="hero-overlay absolute inset-0 pointer-events-none" />
        <div className="absolute bottom-8 right-0 font-display text-white pointer-events-none select-none"
          style={{ fontSize: "clamp(100px,18vw,220px)", fontWeight: 300, opacity: 0.025, lineHeight: 1 }}>CARE</div>
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-16 items-center w-full">
          {/* Left */}
          <div>
            <FadeUp delay={0.1}>
              <div className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-medium uppercase tracking-widest mb-7"
                style={{ background: "rgba(200,169,110,0.12)", border: "1px solid rgba(200,169,110,0.3)", color: "#c8a96e" }}>
                <span style={{ fontSize: 8 }}>●</span> Health & Social Care Staffing
              </div>
            </FadeUp>
            <FadeUp delay={0.2}>
              <h1 className="font-display text-white leading-tight mb-6" style={{ fontSize: "clamp(38px,5vw,68px)", fontWeight: 300 }}>
                Staffing with <em className="gold" style={{ fontStyle: "italic" }}>Understanding,</em><br />
                Delivering with <em style={{ fontStyle: "italic", color: "#e8cfa0" }}>Compassion</em>
              </h1>
            </FadeUp>
            <FadeUp delay={0.3}>
              <p className="text-base leading-relaxed mb-10 max-w-lg" style={{ color: "rgba(255,255,255,0.62)" }}>
                We don't just supply staff — we understand care. Built on real sector experience, EmpathCare Solutions matches the right professionals to the right environments, every time.
              </p>
            </FadeUp>
            <FadeUp delay={0.4}>
              <div className="flex flex-wrap gap-4 items-center">
                <button onClick={() => scrollTo("contact")}
                  className="gold-bg text-slate-900 text-sm font-semibold uppercase tracking-wider px-8 py-3.5 rounded-sm hover:brightness-110 transition-all duration-200"
                  style={{ boxShadow: "0 8px 24px rgba(200,169,110,0.3)" }}>
                  Partner With Us
                </button>
                <button onClick={() => scrollTo("why")}
                  className="text-white/75 hover:text-yellow-300 text-sm font-medium flex items-center gap-2 transition-colors duration-200">
                  Discover Our Difference <span>→</span>
                </button>
              </div>
            </FadeUp>
          </div>
          {/* Right — stat cards */}
          <div className="flex flex-col gap-5">
            {[
              { num: "100%", label: "Vetted & Values-Driven Professionals", delay: 0.25 },
              { num: "24/7", label: "Responsive Staffing Support", delay: 0.4 },
              { num: "CQC", label: "Compliant Across All Placements", delay: 0.55 },
            ].map((s, i) => (
              <FadeUp key={i} delay={s.delay} className={i === 1 ? "md:ml-10" : ""}>
                <div className="rounded-lg p-6" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
                  <div className="font-display gold leading-none mb-2" style={{ fontSize: 44, fontWeight: 600 }}>{s.num}</div>
                  <div className="text-sm tracking-wide" style={{ color: "rgba(255,255,255,0.5)" }}>{s.label}</div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <div className="strip-bg py-5 px-6">
        <div className="max-w-7xl mx-auto flex flex-wrap justify-center gap-6 md:gap-10">
          {["Compassion-Led Recruitment", "Person-Centred Staffing", "Safeguarding Compliant", "Emergency Cover Available", "Long-Term Relationship Focus"].map(item => (
            <div key={item} className="flex items-center gap-2 text-white/90 text-sm font-medium">
              <span className="text-green-300 font-bold">✓</span> {item}
            </div>
          ))}
        </div>
      </div>

      {/* WHY CHOOSE US */}
      <section id="why" className="bg-white py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <FadeUp>
            <p className="teal text-xs font-semibold uppercase tracking-widest mb-3">Why Choose Us</p>
            <h2 className="font-display navy leading-tight mb-4" style={{ fontSize: "clamp(30px,4vw,50px)", fontWeight: 300 }}>
              Real Care Experience.<br /><em className="teal" style={{ fontStyle: "italic" }}>Real Understanding.</em>
            </h2>
            <p className="text-slate-500 text-base leading-relaxed max-w-lg">
              Our foundation is built on genuine hands-on experience in health and social care — the insight that shapes everything we do.
            </p>
          </FadeUp>
          <div className="mt-14 grid md:grid-cols-2 rounded-sm overflow-hidden" style={{ border: "1px solid #e8e2d8" }}>
            {WHY_CARDS.map((c, i) => (
              <FadeUp key={i} delay={i * 0.1}>
                <div className="why-card p-10 relative h-full" style={{
                  borderRight: i % 2 === 0 ? "1px solid #e8e2d8" : "none",
                  borderBottom: i < 2 ? "1px solid #e8e2d8" : "none"
                }}>
                  <span className="font-display absolute top-5 right-6 leading-none select-none"
                    style={{ fontSize: 64, fontWeight: 300, color: "rgba(26,107,90,0.07)" }}>{c.num}</span>
                  <div className="w-12 h-12 rounded-md flex items-center justify-center text-2xl mb-5"
                    style={{ background: "linear-gradient(135deg, #1a6b5a, #2a9070)" }}>{c.icon}</div>
                  <h3 className="font-display navy text-xl font-semibold mb-3">{c.title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{c.text}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* APPROACH */}
      <section id="approach" className="navy-bg py-24 px-6">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          <FadeUp>
            <p className="gold text-xs font-semibold uppercase tracking-widest mb-3">Our Philosophy</p>
            <h2 className="font-display text-white leading-tight mb-4" style={{ fontSize: "clamp(30px,4vw,50px)", fontWeight: 300 }}>
              We Understand<br /><em className="gold" style={{ fontStyle: "italic" }}>Your Challenges</em>
            </h2>
            <p className="text-base leading-relaxed mb-8 max-w-lg" style={{ color: "rgba(255,255,255,0.55)" }}>
              From last-minute staffing gaps to maintaining continuity of care, we know the operational pressures you face. Our responsive approach ensures dependable support exactly when you need it.
            </p>
            <blockquote className="pl-6" style={{ borderLeft: "3px solid #c8a96e" }}>
              <p className="font-display text-xl font-light leading-relaxed" style={{ color: "rgba(255,255,255,0.82)", fontStyle: "italic" }}>
                "Care is not just a service — it's about people. We ensure our staff share the same commitment to dignity, respect, and empathy that your organisation stands for."
              </p>
            </blockquote>
          </FadeUp>
          <div className="grid grid-cols-2 gap-4">
            {APPROACH_CARDS.map((c, i) => (
              <FadeUp key={i} delay={i * 0.15} className={i === 0 ? "col-span-2" : ""}>
                <div className="pillar-card rounded-lg p-6 h-full" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
                  <div className="text-3xl mb-3">{c.icon}</div>
                  <h4 className="text-sm font-semibold text-white mb-2 tracking-wide">{c.title}</h4>
                  <p className="text-xs leading-relaxed" style={{ color: "rgba(255,255,255,0.45)" }}>{c.text}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="py-24 px-6" style={{ background: "#f2ede4" }}>
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 gap-6">
            <FadeUp>
              <p className="teal text-xs font-semibold uppercase tracking-widest mb-3">What We Offer</p>
              <h2 className="font-display navy leading-tight" style={{ fontSize: "clamp(30px,4vw,50px)", fontWeight: 300 }}>
                Staffing Solutions<br /><em className="teal" style={{ fontStyle: "italic" }}>Built Around You</em>
              </h2>
            </FadeUp>
            <button onClick={() => scrollTo("contact")}
              className="gold-bg text-slate-900 text-xs font-semibold uppercase tracking-wider px-7 py-3 rounded-sm hover:brightness-110 transition-all duration-200 self-start md:self-end">
              Discuss Your Needs
            </button>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => (
              <FadeUp key={i} delay={i * 0.08}>
                <div className="service-card bg-white rounded-md p-9 relative overflow-hidden h-full" style={{ border: "1px solid #e8e2d8" }}>
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 origin-left scale-x-0 hover:scale-x-100 transition-transform duration-300"
                    style={{ background: "linear-gradient(90deg, #1a6b5a, #c8a96e)" }} />
                  <div className="text-4xl mb-5">{s.icon}</div>
                  <h3 className="font-display navy text-xl font-semibold mb-3">{s.title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{s.text}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* FOUR PILLARS */}
      <section className="bg-white py-24 px-6 text-center">
        <div className="max-w-7xl mx-auto">
          <FadeUp>
            <p className="teal text-xs font-semibold uppercase tracking-widest mb-3">Our Commitment</p>
            <h2 className="font-display navy leading-tight mx-auto" style={{ fontSize: "clamp(30px,4vw,50px)", fontWeight: 300 }}>
              The Four Pillars of<br /><em className="teal" style={{ fontStyle: "italic" }}>EmpathCare</em>
            </h2>
          </FadeUp>
          <div className="mt-14 grid md:grid-cols-4 rounded-sm overflow-hidden" style={{ border: "1px solid #e8e2d8" }}>
            {PILLARS.map((p, i) => (
              <FadeUp key={i} delay={i * 0.1}>
                <div className="pillar-card p-10 h-full" style={{ borderRight: i < 3 ? "1px solid #e8e2d8" : "none", border: "1px solid #e8e2d8" }}>
                  <div className="text-4xl mb-4">{p.icon}</div>
                  <h4 className="font-display navy text-xl font-semibold mb-3">{p.title}</h4>
                  <p className="text-slate-500 text-sm leading-relaxed">{p.text}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* CTA / CONTACT */}
      <section id="contact" className="cta-bg py-24 px-6">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-16 items-start">
          <FadeUp>
            <p className="gold text-xs font-semibold uppercase tracking-widest mb-3">Get Started</p>
            <h2 className="font-display text-white leading-tight mb-4" style={{ fontSize: "clamp(30px,4vw,50px)", fontWeight: 300 }}>
              Ready to Work<br />With <em className="gold" style={{ fontStyle: "italic" }}>EmpathCare?</em>
            </h2>
            <p className="text-base leading-relaxed mb-10 max-w-md" style={{ color: "rgba(255,255,255,0.6)" }}>
              Whether you're an organisation seeking exceptional care staff or a care professional looking for your next placement, we'd love to hear from you.
            </p>
            <div className="flex flex-col gap-5">
              {[
                { icon: "📞", label: "+44 (0) 7000 000 000" },
                { icon: "✉️", label: "hello@empathcaresolutions.co.uk" },
                { icon: "🌐", label: "empathcaresolutions.co.uk" },
              ].map((c) => (
                <div key={c.label} className="flex items-center gap-3 text-sm" style={{ color: "rgba(255,255,255,0.7)" }}>
                  <span className="text-xl gold">{c.icon}</span> {c.label}
                </div>
              ))}
            </div>
          </FadeUp>
          <FadeUp delay={0.2}>
            <div className="rounded-lg p-8" style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)" }}>
              <h3 className="font-display text-white text-2xl font-light mb-7">Send Us a Message</h3>
              <div className="grid grid-cols-2 gap-3 mb-3">
                {["First Name", "Last Name"].map(p => (
                  <input key={p} placeholder={p} className="w-full px-4 py-3 rounded-sm text-sm text-white"
                    style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)", fontFamily: "'DM Sans',sans-serif" }} />
                ))}
              </div>
              {["Email Address", "Organisation / Company"].map(p => (
                <input key={p} placeholder={p} className="w-full px-4 py-3 rounded-sm text-sm text-white mb-3"
                  style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)", fontFamily: "'DM Sans',sans-serif" }} />
              ))}
              <select className="w-full px-4 py-3 rounded-sm text-sm mb-3"
                style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.7)", fontFamily: "'DM Sans',sans-serif" }}>
                <option value="">I am enquiring as a...</option>
                <option>Care Provider / Organisation</option>
                <option>Healthcare Professional</option>
                <option>Social Care Professional</option>
                <option>Other</option>
              </select>
              <input placeholder="How can we help?" className="w-full px-4 py-3 rounded-sm text-sm text-white mb-5"
                style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)", fontFamily: "'DM Sans',sans-serif" }} />
              <a href="mailto:hello@empathcaresolutions.co.uk"
                className="block w-full text-center gold-bg text-slate-900 text-xs font-semibold uppercase tracking-wider py-3.5 rounded-sm hover:brightness-110 transition-all duration-200">
                Send Enquiry
              </a>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="navy-bg px-6 pt-16 pb-8" style={{ borderTop: "1px solid rgba(200,169,110,0.18)" }}>
        <div className="max-w-7xl mx-auto grid md:grid-cols-4 gap-12 mb-12">
          <div className="md:col-span-2 lg:col-span-1">
            <div className="font-display text-xl font-semibold text-white mb-4">
              Empath<span className="gold">Care</span> Solutions
            </div>
            <p className="text-sm leading-relaxed max-w-xs" style={{ color: "rgba(255,255,255,0.45)" }}>
              Staffing with Understanding, Delivering with Compassion. Serving health and social care providers across the UK with insight, integrity, and dedication.
            </p>
          </div>
          {[
            { title: "Services", links: ["Residential Care", "Domiciliary Care", "Specialist Support", "Emergency Cover", "Permanent Recruitment"] },
            { title: "Company", links: ["About Us", "Our Approach", "For Professionals", "For Employers", "Contact"] },
            { title: "Compliance", links: ["Privacy Policy", "Terms of Service", "Safeguarding Policy", "GDPR"] },
          ].map(col => (
            <div key={col.title}>
              <h5 className="gold text-xs font-semibold uppercase tracking-widest mb-5">{col.title}</h5>
              <ul className="flex flex-col gap-2.5">
                {col.links.map(l => (
                  <li key={l}><a href="#" className="text-sm transition-colors duration-200 hover:text-yellow-300" style={{ color: "rgba(255,255,255,0.5)" }}>{l}</a></li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 pt-6" style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
          <p className="text-xs" style={{ color: "rgba(255,255,255,0.3)" }}>
            © 2025 EmpathCare Solutions Ltd. All rights reserved. Registered in England & Wales.
          </p>
          <div className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-medium"
            style={{ background: "rgba(200,169,110,0.1)", border: "1px solid rgba(200,169,110,0.22)", color: "#c8a96e" }}>
            🛡️ CQC Compliant Staffing Agency
          </div>
        </div>
      </footer>
    </div>
  );
}